/**
 * v2 slide renderer — compiles a (master + template + theme) from the md-first
 * slide system (src/lib/slide-system.generated.json) into a full slide HTML
 * document, the same shape the deck viewer/editor + exports already consume.
 *
 * The system JSON is generated from rules/standards/deck-design/system/*.md by
 * scripts/build-slide-system.mjs, so layout defaults (paddings, zone heights,
 * element defaults, themes, chrome) all trace back to editable markdown.
 *
 * Model: a slide has four zones (background / header / main / footer); a
 * template places a tree of FRAMES (the one element primitive: text, rectangle,
 * ellipse, image) into those zones. A frame carries fill / per-corner radius /
 * padding / 9-point align / auto-layout, and can nest. The background theme sets
 * the slide fill + the default high-contrast foreground; the chrome layer adds
 * the locked brand furniture (logo, symbol, page number, copyright).
 */
import system from './slide-system.generated.json';

export const slideSystem = system;
const CANVAS_H = system.tokens?.canvas?.height || 1080;

// Per-side SAFE-AREA MARGINS (the magenta guide lines). Authored in tokens.md
// (grid.margins, or the uniform grid.margin), with a live sandbox override so the
// Foundation editor can move the lines without a rebuild.
let MARGIN_OVERRIDE = null;
export function setMarginOverride(m) { MARGIN_OVERRIDE = (m && Object.keys(m).length) ? m : null; }
export function getMargins() {
  const g = system.tokens?.grid || {};
  const base = g.margin ?? 40;
  return { top: base, right: base, bottom: base, left: base, ...(g.margins || {}), ...(MARGIN_OVERRIDE || {}) };
}
/** tokens.md `grid.margins:` line for the given per-side margins (commit path). */
export function marginsTokenMd(m) {
  return `  margins: { top: ${Math.round(m.top)}, right: ${Math.round(m.right)}, bottom: ${Math.round(m.bottom)}, left: ${Math.round(m.left)} }   # per-side safe area — add under grid:`;
}

// Collect the text-frame roles (+ their default text as a hint) of a frame tree.
function textRoles(frames, out = []) {
  for (const f of frames || []) {
    if (f.el === 'text' && f.role) out.push({ role: f.role, hint: f.text || '' });
    if (f.children) textRoles(f.children, out);
  }
  return out;
}
/** The default zone tokens, as the authoring panel's starting overrides. */
export function defaultZoneOverrides() {
  const z = system.tokens?.zones || {};
  const cp = (p) => ({ top: p?.top || 0, right: p?.right || 0, bottom: p?.bottom || 0, left: p?.left || 0 });
  return {
    margin: system.tokens?.grid?.margin ?? 40,
    zones: {
      header: { height: z.header?.height ?? 360, padding: cp(z.header?.padding) },
      main: { padding: cp(z.main?.padding) },
      footer: { height: z.footer?.height ?? 120, padding: cp(z.footer?.padding) },
      background: { padding: cp(z.background?.padding) },
    },
  };
}
/** Emit the `grid:` + `zones:` sections of tokens.md for the given overrides,
 *  so an authored change can be committed back to the md (the canonical source). */
export function tokensMd(ov) {
  const g = system.tokens?.grid || {};
  const p = (pad) => `{ top: ${pad.top || 0}, right: ${pad.right || 0}, bottom: ${pad.bottom || 0}, left: ${pad.left || 0} }`;
  const z = ov.zones;
  return `grid:
  margin: ${ov.margin}        # overall side padding (left/right/top/bottom safe area)
  gutter: ${g.gutter ?? 27}
  columns: ${g.columns ?? 6}
  column: ${g.column ?? 279.5}
zones:
  background:
    padding: ${p(z.background.padding)}
  header:
    height: ${z.header.height}
    optional: true
    padding: ${p(z.header.padding)}
  main:
    padding: ${p(z.main.padding)}
  footer:
    height: ${z.footer.height}
    optional: true
    padding: ${p(z.footer.padding)}`;
}

/** A compact catalogue of masters → templates (with their fillable text roles),
 *  for the AI "pick the format + write the content" call. */
export function slideCatalog() {
  return Object.values(system.masters).map(m => ({
    id: m.id, label: m.label, zones: m.zones,
    templates: Object.values(system.templates[m.id] || {}).map(t => ({
      id: t.id, label: t.label, roles: textRoles(t.frames),
    })),
  }));
}

function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// Hex of the brand palette tokens — used to compute auto-contrast (which text
// colour reads on a given fill). Mirrors colors_and_type.css.
const PALETTE_HEX = {
  white: '#FFFFFF', black: '#202020', orange: '#FF6428', 'orange-deep': '#DE3015',
  'warm-grey': '#F3ECE6', 'warm-grey-light': '#FCF8F7', taupe: '#C9BCB0',
  'warm-grey-text': '#93867D', 'warm-grey-deep': '#6C5F5B', 'warm-grey-darker': '#4C3F3B',
  'support-purple': '#715AAE', 'support-red': '#E4091F', 'support-green': '#00AF37',
  'support-blue-light': '#BBD2FF', 'support-peach': '#FFC3B1',
};
function hexOf(token) {
  if (typeof token === 'string' && token.startsWith('#')) return token;
  return PALETTE_HEX[token] || '#F3ECE6';
}
function luminance(hex) {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.replace('#', '') ? `#${hex.replace('#', '')}` : hex);
  if (!m) return 0.6;
  const lin = (c) => { c = parseInt(c, 16) / 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
  return 0.2126 * lin(m[1]) + 0.7152 * lin(m[2]) + 0.0722 * lin(m[3]);
}
// THE AUTO-CONTRAST RULE (backgrounds.md): black or white against a fill, with
// the brand exception that an orange fill always pairs with BLACK.
function autoContrast(bgToken) {
  if (bgToken === 'orange') return 'black';
  return luminance(hexOf(bgToken)) > 0.55 ? 'black' : 'white';
}

// Resolve a fill/colour token to a CSS value, in the context of a theme and the
// background it sits on. Handles SEMANTIC roles so templates stay theme-agnostic:
//   fg → auto-contrast against the context bg · muted/surface/accent → theme · none/scrim/palette as-is.
function color(token, theme, ctxBg) {
  if (token == null || token === 'none') return 'transparent';
  if (token === 'scrim') return 'linear-gradient(120deg, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.30) 46%, rgba(0,0,0,0.10) 100%)';
  const t = resolveToken(token, theme, ctxBg);
  if (typeof t === 'string' && (t.startsWith('#') || t.startsWith('rgb') || t.startsWith('var('))) return t;
  return `var(--${t})`;
}
// Map a semantic token to a concrete palette token / hex for the active theme.
function resolveToken(token, theme, ctxBg) {
  if (!theme) return token;
  if (token === 'fg' || token === 'auto') return autoContrast(ctxBg ?? theme.fill);
  if (token === 'muted') return theme.muted || 'warm-grey-deep';
  if (token === 'surface') return theme.surface || 'white';
  if (token === 'accent') return theme.accent || 'orange';
  return token;
}
function radius(r) {
  if (!Array.isArray(r)) return '0';
  return r.map(v => (typeof v === 'number' ? `${v}px` : String(v))).join(' ');
}
function pad(p) {
  if (!p) return '0';
  return `${p.top || 0}px ${p.right || 0}px ${p.bottom || 0}px ${p.left || 0}px`;
}
const JUSTIFY = { left: 'flex-start', center: 'center', right: 'flex-end', top: 'flex-start', bottom: 'flex-end' };
function fontCss(f, theme, ctxBg) {
  if (!f) return '';
  const fam = f.family === 'display' ? 'var(--font-display)' : 'var(--font-sans)';
  const parts = [`font-family:${fam}`];
  if (f.weight != null) parts.push(`font-weight:${f.weight}`);
  if (f.size != null) parts.push(`font-size:${f.size}px`);
  if (f.lineHeight != null) parts.push(`line-height:${f.lineHeight}`);
  if (f.tracking != null) parts.push(`letter-spacing:${f.tracking}em`);
  // Default text colour = auto-contrast against the background it sits on.
  parts.push(`color:${color(f.color != null ? f.color : 'fg', theme, ctxBg)}`);
  return parts.join(';');
}

// The named brand type style referenced by `set.style` (the single source for
// text size/weight/colour). Templates use these instead of raw px so nothing
// drifts from the brand.
function typeStyle(name) {
  return (system.typeStyles && system.typeStyles.styles && system.typeStyles.styles[name]) || null;
}

// Merge an element's defaults with a frame's `set` overrides (shallow + font
// deep). Font precedence: element default → named brand type style → explicit
// set.font — so `style:` sets the brand base and per-frame font tweaks win.
function settings(elDef, set) {
  const d = (elDef && elDef.defaults) || {};
  const s = set || {};
  const styleFont = s.style ? typeStyle(s.style) : null;
  return {
    fill: s.fill !== undefined ? s.fill : d.fill,
    radius: s.radius || d.radius || [0, 0, 0, 0],
    padding: s.padding || d.padding || { top: 0, right: 0, bottom: 0, left: 0 },
    align: s.align || d.align || { h: 'left', v: 'top' },
    layout: s.layout || d.layout || 'none',
    gap: s.gap != null ? s.gap : (d.gap || 0),
    font: { ...(d.font || {}), ...(styleFont || {}), ...(s.font || {}) },
    style: s.style || null,
    fit: s.fit || d.fit,
    src: s.src !== undefined ? s.src : d.src,
  };
}

// Render one frame (recursive). `flexChild` = the parent is an auto-layout
// container, so this frame flows. `ctxBg` is the background token this frame
// sits ON (for auto-contrast); a frame with its own fill becomes the ctxBg for
// its children.
function renderFrame(frame, flexChild, theme, ctxBg, content, originY) {
  const elDef = system.elements[frame.el] || {};
  const st = settings(elDef, frame.set);
  const isImage = frame.el === 'image' || st.fill === 'image';
  const css = ['box-sizing:border-box'];

  if (flexChild) {
    if (frame.rect && frame.rect.w) css.push(`width:${frame.rect.w}px`);
    if (frame.rect && frame.rect.h) css.push(`height:${frame.rect.h}px`);
    css.push('flex:0 0 auto');
    if (st.layout !== 'none' && (frame.children || []).length) css.push('flex:1 1 0');
  } else {
    // Template rects are authored in CANVAS coords; a frame lives inside its
    // zone (offset by the zone's top), so subtract the zone origin so it lands
    // at the authored canvas position instead of being pushed down by the band.
    const r = frame.rect || { x: 0, y: 0, w: 100, h: 100 };
    css.push('position:absolute', `left:${r.x}px`, `top:${r.y - (originY || 0)}px`, `width:${r.w}px`, `height:${r.h}px`);
  }

  // Fill (colour or image) + the background token children inherit for contrast.
  const hasFill = !isImage && st.fill && st.fill !== 'none' && st.fill !== 'scrim';
  let childCtx = ctxBg;
  if (isImage && st.src) { css.push(`background:#000 url('${esc(st.src)}') center/${st.fit || 'cover'} no-repeat`); childCtx = 'black'; }
  else if (st.fill === 'scrim') { css.push(`background:${color('scrim')}`); childCtx = 'black'; }
  else if (hasFill) { css.push(`background:${color(st.fill, theme, ctxBg)}`); childCtx = resolveToken(st.fill, theme, ctxBg); }
  css.push(`border-radius:${radius(st.radius)}`);
  if (st.padding) css.push(`padding:${pad(st.padding)}`);

  // Auto-layout for children.
  const kids = frame.children || [];
  if (st.layout === 'row' || st.layout === 'column') {
    css.push('display:flex', `flex-direction:${st.layout}`, `gap:${st.gap}px`);
    css.push(`justify-content:${JUSTIFY[st.align.h] || 'flex-start'}`);
    css.push(`align-items:${JUSTIFY[st.align.v] || 'flex-start'}`);
  } else if (kids.length) {
    css.push('position:relative');   // anchor absolutely-placed children
  }

  // Text frames carry the font + content; their colour auto-contrasts childCtx.
  const isText = frame.el === 'text';
  if (isText) css.push(fontCss(st.font, theme, childCtx));

  // AI-/caller-supplied content overrides a text frame's default by role.
  const overrideTxt = (content && frame.role && content[frame.role] != null) ? content[frame.role] : frame.text;
  const inner = isText
    ? esc(overrideTxt || '')
    : kids.map(k => renderFrame(k, st.layout === 'row' || st.layout === 'column', theme, childCtx, content)).join('');

  const dataRole = frame.role ? ` data-role="${esc(frame.role)}"` : '';
  const dataStyle = st.style ? ` data-style="${esc(st.style)}"` : '';
  return `<div class="fr fr--${esc(frame.el)}"${dataRole}${dataStyle} style="${css.join(';')}">${inner}</div>`;
}

// The OVERALL MASTER layer — brand furniture that applies to every slide and
// stays locked: the corner symbol, the bottom-left logo, the copyright and the
// page number. A full-slide overlay above the zones so it always paints on top.
// FURNITURE GEOMETRY — each chrome item's {x, y, size, align}. Derived from the
// item's position/margin/size in chrome.md (or its `geom:` block), with a live
// sandbox override so the master editor can move/resize/align it without rebuild.
let CHROME_OVERRIDE = null;
export function setChromeOverride(m) { CHROME_OVERRIDE = (m && Object.keys(m).length) ? m : null; }
export function chromeItemKind(name) { return (name === 'symbol' || name === 'logo') ? 'image' : 'text'; }
// Natural aspect (w/h) of the image furniture, so the box matches the real asset
// (eraneos wordmark 933×139, symbol square). null = not an image.
export function chromeItemAspect(name) {
  if (name === 'logo') return 933 / 139;
  if (name === 'symbol') return 209.67 / 209.67;
  return null;
}
function deriveChromeGeom(name) {
  const it = system.chrome?.items?.[name]; if (!it) return null;
  const W = system.tokens?.canvas?.width || 1920; const H = CANVAS_H;
  const m = it.margin ?? 40;
  const kind = chromeItemKind(name);
  const fontSize = it.font?.size ?? it.size ?? 24;
  // Box width + height. Images default to the asset's real aspect; text's box is
  // its line height, with font/fontSize separate and 9-point inner alignment.
  const h = kind === 'image' ? (it.height ?? it.size ?? 40) : Math.round(fontSize * 1.3);
  const w = kind === 'image' ? Math.round(h * chromeItemAspect(name))
    : (name === 'title' ? 760 : name === 'copyright' ? 360 : 80);
  const [vert, horiz] = (it.position || 'bottom-left').split('-');
  const hA = horiz === 'center' ? 'center' : horiz === 'right' ? 'right' : 'left';
  // x,y is the box top-left (no anchor offset).
  const x = horiz === 'center' ? Math.round(W / 2 - w / 2) : horiz === 'right' ? W - m - w : m;
  const y = vert === 'top' ? m : H - m - h;
  const base = { x, y, w, h, align: kind === 'image' ? 'center-middle' : `${hA}-middle` };
  if (kind === 'text') { base.font = 'sans'; base.fontSize = fontSize; }
  return { ...base, ...(it.geom || {}) };
}
export function getChromeGeom(name) {
  const base = deriveChromeGeom(name); if (!base) return null;
  const ov = CHROME_OVERRIDE && CHROME_OVERRIDE[name];
  return ov ? { ...base, ...ov } : base;
}
/** chrome.md `geom:` lines for each overridden item (commit path). */
export function chromeItemsMd(ov) {
  const r = Math.round;
  return Object.entries(ov || {}).map(([name, g]) => {
    let s = `x: ${r(g.x)}, y: ${r(g.y)}, w: ${r(g.w)}, h: ${r(g.h)}, align: ${g.align}`;
    if (chromeItemKind(name) === 'text') s += `, font: ${g.font || 'sans'}, fontSize: ${r(g.fontSize || 24)}`;
    return `  ${name}:\n    geom: { ${s} }`;
  }).join('\n');
}
// 9-point alignment → flex (content within the box).
const JUST9 = { left: 'flex-start', center: 'center', right: 'flex-end' };
const ALV9 = { top: 'flex-start', middle: 'center', bottom: 'flex-end' };
export function parseAlign9(a) { const p = String(a || 'center-middle').split('-'); return [JUST9[p[0]] ? p[0] : 'center', ALV9[p[1]] ? p[1] : 'middle']; }

function renderCorner(master, theme, page, logoSetOverride, furnitureOverride, deckTitle) {
  const ch = system.chrome;
  if (!ch) return '';
  const scope = furnitureOverride || (ch.scope && (ch.scope[master.id] || ch.scope.default)) || [];
  // Eraneos ⇄ Eve is a deck-wide logo toggle.
  const logoSet = logoSetOverride || system.masterSlides?.logoSet || 'eraneos';
  const assets = (ch.assets && ch.assets[theme.logo]) || {};
  const filter = assets.filter && assets.filter !== 'none' ? `filter:${assets.filter};` : '';
  const fg = color('fg', theme, theme.fill);
  const out = [];
  // overflow:hidden clips each chrome box to its reserved geom — a long deck
  // label truncates (…) inside its own width instead of overprinting the © notice.
  const box = (name, inner) => { const g = getChromeGeom(name); const [hA, vA] = parseAlign9(g.align); return `<div class="chrome__${name}" style="position:absolute;left:${g.x}px;top:${g.y}px;width:${g.w}px;height:${g.h}px;display:flex;justify-content:${JUST9[hA]};align-items:${ALV9[vA]};overflow:hidden">${inner(g, hA)}</div>`; };
  const img = (name, src, alt) => out.push(box(name, () => `<img src="${esc(src)}" alt="${esc(alt)}" style="max-width:100%;max-height:100%;width:auto;height:auto;object-fit:contain;${filter}">`));
  const txt = (name, content) => out.push(box(name, (g, hA) => { const it = ch.items[name]; const fam = g.font === 'display' ? 'var(--font-display)' : 'var(--font-sans)'; return `<span style="font-family:${fam};font-weight:${it.font?.weight || 450};font-size:${g.fontSize || it.font?.size || 24}px;line-height:1;text-align:${hA};color:${fg};white-space:nowrap;max-width:100%;overflow:hidden;text-overflow:ellipsis">${esc(content)}</span>`; }));
  if (scope.includes('symbol') && assets.symbol) img('symbol', assets.symbol, '');
  if (scope.includes('logo') && assets.wordmark) img('logo', logoSet === 'eve' ? '/brand/assets/eve-by-eraneos-logo.png' : assets.wordmark, logoSet === 'eve' ? 'eve by eraneos' : 'eraneos');
  if (scope.includes('title') && ch.items.title) txt('title', deckTitle != null ? deckTitle : (ch.items.title.text || ''));
  if (scope.includes('copyright') && ch.items.copyright) txt('copyright', (ch.items.copyright.text || '').replace('{year}', new Date().getFullYear()));
  if (scope.includes('pageNumber') && ch.items.pageNumber) txt('pageNumber', page || '');
  if (!out.length) return '';
  return `<div class="chrome" style="position:absolute;inset:0;z-index:10;pointer-events:none">${out.join('')}</div>`;
}

// The footer zone's default content: a small, editable SOURCES & NOTES line
// (real .fr text frame) at the top of the footer band, sitting on its own layer
// above the master chrome.
function footerDefault(theme, content) {
  const f = typeStyle('footnote') || { family: 'sans', weight: 450, size: 18, lineHeight: 1.3 };
  const txt = (content && content.sources != null) ? content.sources : 'Sources & notes';
  return `<div class="fr fr--text" data-role="sources" data-style="footnote" style="position:absolute;left:40px;top:8px;width:1700px;box-sizing:border-box;${fontCss(f, theme, theme.fill)}">${esc(txt)}</div>`;
}

/** Render a BARE MASTER SLIDE (Level 1): just its background + the locked chrome
 *  furniture it carries — NO zones, NO template, NO body text. Zones arrive with
 *  a template at Level 2; text elements live inside those at Level 4. A master
 *  slide owns only its background and the colour/logo logic for that ground. */
// Build the shared pieces of a master slide (theme, base fill, background layers,
// chrome) — used by the bare master render AND the Level-2 skeleton render.
function masterParts(masterSlideId, { logoSet, sampleImage, def, deckTitle, furniture } = {}) {
  const ms = def || system.masterSlides?.slides?.[masterSlideId];
  if (!ms) throw new Error(`unknown master slide "${masterSlideId}"`);
  const bg = ms.background || {};
  const baseFill = bg.fill || bg.base || 'black';
  const theme = {
    fill: baseFill, fg: ms.fg, muted: ms.muted, surface: ms.surface,
    accent: ms.accent, logo: ms.logo?.variant || 'dark', slideClass: ms.slideClass || '',
  };
  let bgLayers = '';
  if (bg.image) {
    const src = sampleImage || '/assets/eraneos-lightroom.png';
    // `transparency` = how much of the black ground shows THROUGH the photo, so
    // 0 → full-bleed image (cover), 30 → image at 70% over black (content slide).
    const op = bg.transparency != null ? Math.max(0, Math.min(1, 1 - bg.transparency / 100)) : 1;
    bgLayers += `<div style="position:absolute;inset:0;background:#000 url('${esc(src)}') center/cover no-repeat;opacity:${op}"></div>`;
    if (ms.scrim) bgLayers += `<div style="position:absolute;inset:0;background:${color('scrim')}"></div>`;
  }
  const chromeHtml = renderCorner({ id: masterSlideId }, theme, '7', logoSet, furniture || ms.furniture || [], deckTitle);
  return { ms, theme, baseFill, bgLayers, chromeHtml };
}

// The safe-area MARGIN guides: thin magenta lines at the left/right/top/bottom
// padding (the content safe area), shown in sandbox previews only. They mark how
// far content sits from each edge — distinct from the cyan zone sections.
function guideLinesHTML() {
  const W = system.tokens?.canvas?.width || 1920;
  const H = CANVAS_H;
  const m = getMargins();
  const c = 'rgba(255,0,128,0.5)';
  const v = (x) => `<div style="position:absolute;left:${x}px;top:0;width:1px;height:${H}px;background:${c}"></div>`;
  const h = (y) => `<div style="position:absolute;top:${y}px;left:0;height:1px;width:${W}px;background:${c}"></div>`;
  return `<div class="guides" style="position:absolute;inset:0;z-index:9;pointer-events:none">${v(m.left)}${v(W - m.right)}${h(m.top)}${h(H - m.bottom)}</div>`;
}

// A 40px raster grid drawn in CANVAS coordinates (exact), so it scales uniformly
// with the slide and lands cleanly on every edge — 1920/40 = 48, 1080/40 = 27.
function rasterGridHTML() {
  const W = system.tokens?.canvas?.width || 1920;
  const H = CANVAS_H;
  const c = 'rgba(255,0,128,0.25)';
  return `<div class="raster" style="position:absolute;inset:0;z-index:8;pointer-events:none;background-image:repeating-linear-gradient(to right, ${c} 0 1px, transparent 1px 40px), repeating-linear-gradient(to bottom, ${c} 0 1px, transparent 1px 40px)"><div style="position:absolute;top:0;right:0;width:1px;height:100%;background:${c}"></div><div style="position:absolute;left:0;bottom:0;height:1px;width:100%;background:${c}"></div></div>`;
}

function slideDoc(inner, { theme, baseFill, dataAttrs = '', guides = false, raster = false }) {
  return `<!doctype html>
<html lang="en"><head>
<meta charset="utf-8">
<link rel="stylesheet" href="/brand/slide.css">
<link rel="stylesheet" href="/brand/slide-system.generated.css">
</head><body>
<div class="stage">
  <div class="slide ${esc(theme.slideClass)}"${dataAttrs} style="color:${color('fg', theme, baseFill)};background:${color(baseFill, theme, baseFill)}">
    ${raster ? rasterGridHTML() : ''}
    ${inner}
    ${guides ? guideLinesHTML() : ''}
  </div>
</div>
<script src="/brand/slide-fit.js"></script>
</body></html>`;
}

export function renderMasterHTML({ masterSlideId, logoSet, sampleImage, def, deckTitle, guides, raster, furniture }) {
  const { theme, baseFill, bgLayers, chromeHtml } = masterParts(masterSlideId, { logoSet, sampleImage, def, deckTitle, furniture });
  return slideDoc(`${bgLayers}\n    ${chromeHtml}`, { theme, baseFill, guides, raster, dataAttrs: ` data-master-slide="${esc(masterSlideId)}"` });
}

/** Render JUST the L1 brand chrome (`<div class="chrome">` — symbol · logo · title
 *  · copyright · page) for a master slide, with the CORRECT page number. The
 *  single source of truth for a slide's footer/logo; used by the render-time
 *  re-apply so an already-stored slide can pick up the current L1 chrome (and the
 *  real page) without re-generating its content. Returns '' on an unknown master. */
export function renderChrome({ masterSlideId, logoSet, deckTitle, page, furniture } = {}) {
  const ms = system.masterSlides?.slides?.[masterSlideId];
  if (!ms) return '';
  const baseFill = ms.background?.fill || ms.background?.base || 'black';
  const theme = {
    fill: baseFill, fg: ms.fg, muted: ms.muted, surface: ms.surface,
    accent: ms.accent, logo: ms.logo?.variant || 'dark', slideClass: ms.slideClass || '',
  };
  return renderCorner({ id: masterSlideId }, theme, page != null ? String(page) : '', logoSet || 'eraneos', furniture || ms.furniture || [], deckTitle);
}

/** The furniture a TEMPLATE shows: an explicit template `furniture` wins; else a
 *  Display archetype is a clean "moment" (logo only); else (Content) it inherits
 *  the master slide's furniture (the full footer). Returns null to mean "inherit". */
export function templateFurniture(tpl) {
  if (Array.isArray(tpl.furniture)) return tpl.furniture;
  if (tpl.group === 'Display') return ['logo'];
  return null;
}

/** Render a Level-2 TEMPLATE as a ZONE SKELETON on a master slide: the master
 *  background + chrome, with the zones the template uses (header / main) drawn as
 *  labelled dashed bands carrying their slot-role chips. NO arrangement (Level 3)
 *  and NO real text (Level 4) — this shows the slot structure only. */
/** The {x,y,w,h} (canvas coords) of each zone a template uses — from the
 *  template's `zoneRects` override if present, else derived from the token zone
 *  heights + the master's footer reservation. Shared by the grid renderer and the
 *  drag/resize editor so they agree. */
export function templateZoneRects(tpl) {
  const W = system.tokens?.canvas?.width || 1920;
  const H = CANVAS_H;
  const m = getMargins();
  const zh = system.tokens?.zones?.header?.height ?? 360;
  const zf = system.tokens?.zones?.footer?.height ?? 120;
  const ms = system.masterSlides?.slides?.[tpl.master] || {};
  const fur = templateFurniture(tpl) || ms.furniture || [];
  const hasFooterChrome = fur.some(f => f === 'copyright' || f === 'pageNumber' || f === 'title');
  const hasHeader = Array.isArray(tpl.zones?.header);
  const mainTop = hasHeader ? zh : m.top;
  const mainBottom = hasFooterChrome ? H - zf : H - m.bottom;
  const innerW = W - m.left - m.right;
  const ov = tpl.zoneRects || {};
  // Default rect per zone: header → the header band; any other zone (main, or a
  // split's text/image) → the body area. An explicit zoneRects override wins.
  const dflt = (z) => z === 'header'
    ? { x: m.left, y: m.top, w: innerW, h: zh - m.top }
    : { x: m.left, y: mainTop, w: innerW, h: mainBottom - mainTop };
  const rects = {};
  for (const z of Object.keys(tpl.zones || {})) rects[z] = ov[z] || dflt(z);
  return rects;
}

export function zonesetDef(id) { return system.zonesets?.zonesets?.[id] || null; }
export function layoutDef(id) { return system.layouts?.layouts?.[id] || null; }
/** Resolve a Level-3 LAYOUT onto its Level-2 ZONE-SET: the zone-set provides the
 *  structure (zone keys + geometry + default master); the layout fills the slot
 *  roles and may override the master / per-zone alignment. Returns a template-
 *  shaped def the skeleton renderer consumes. */
export function resolveLayout(idOrDef) {
  const l = typeof idOrDef === 'string' ? layoutDef(idOrDef) : idOrDef;
  if (!l) return null;
  const zs = zonesetDef(l.zoneset) || {};
  const zoneKeys = Object.keys(zs.zones || {});
  // Ground is bounded by the zone-set's allowed `grounds` (Layer 2). A layout
  // picks one from that set; an out-of-set master falls back to the default.
  const grounds = zs.grounds || [];
  let master = l.master || zs.master;
  if (grounds.length && !grounds.includes(master)) master = zs.master || grounds[0];
  return {
    label: l.label, group: l.group, zoneset: l.zoneset, note: l.note, grounds,
    master,
    furniture: l.furniture || zs.furniture,
    zones: Object.fromEntries(zoneKeys.map(z => [z, (l.zones && l.zones[z]) || []])),
    zoneRects: { ...(zs.zoneRects || {}), ...(l.zoneRects || {}) },
    zoneAlign: { ...(zs.zoneAlign || {}), ...(l.zoneAlign || {}) },
  };
}
/** zones.md entry for ONE template (commit path) — zones carry the allowed elements. */
export function zonesetMd(id, z) {
  const zoneStr = '{ ' + Object.entries(z.zones || {}).map(([k, roles]) => `${k}: [${(roles || []).join(', ')}]`).join(', ') + ' }';
  const L = [`  ${id}:`, `    label: ${z.label || id}`];
  if (z.group) L.push(`    group: ${z.group}`);
  L.push(`    master: ${z.master}`);
  L.push(`    zones: ${zoneStr}`);
  if (z.zoneRects && Object.keys(z.zoneRects).length) {
    const r = (o) => `{ x: ${Math.round(o.x)}, y: ${Math.round(o.y)}, w: ${Math.round(o.w)}, h: ${Math.round(o.h)} }`;
    L.push(`    zoneRects: { ${Object.entries(z.zoneRects).map(([k, o]) => `${k}: ${r(o)}`).join(', ')} }`);
  }
  if (z.zoneAlign && Object.keys(z.zoneAlign).length) L.push(`    zoneAlign: { ${Object.entries(z.zoneAlign).map(([k, a]) => `${k}: ${a}`).join(', ')} }`);
  if (z.note) L.push(`    note: ${z.note}`);
  return L.join('\n');
}
/** layouts.md entry for ONE layout (commit path). */
export function layoutMd(id, l) {
  const zoneStr = '{ ' + Object.entries(l.zones || {}).map(([k, roles]) => `${k}: [${(roles || []).join(', ')}]`).join(', ') + ' }';
  const L = [`  ${id}:`, `    label: ${l.label || id}`, `    group: ${l.group || 'Content'}`, `    zoneset: ${l.zoneset}`];
  if (l.master) L.push(`    master: ${l.master}`);
  L.push(`    zones: ${zoneStr}`);
  if (l.zoneAlign && Object.keys(l.zoneAlign).length) L.push(`    zoneAlign: { ${Object.entries(l.zoneAlign).map(([k, a]) => `${k}: ${a}`).join(', ')} }`);
  if (l.note) L.push(`    note: ${l.note}`);
  return L.join('\n');
}

export function renderTemplateSkeletonHTML({ templateId, masterSlideId, logoSet, def, guides, raster }) {
  const tpl = def;
  if (!tpl) throw new Error(`renderTemplateSkeletonHTML needs a def (${templateId})`);
  const msId = masterSlideId || tpl.master;
  const parts = masterParts(msId, { logoSet, furniture: templateFurniture(tpl) || undefined });
  const { theme, baseFill, bgLayers, chromeHtml } = parts;

  // Sections are drawn in the editor's CYAN so they read as system overlay (not
  // content), independent of the master's fg.
  const CYAN = '#00B8D9';
  const rects = templateZoneRects({ ...tpl, master: msId });
  const zones = tpl.zones || {};

  const band = (name, r, roles, align) => {
    const chips = (roles || []).map(rr =>
      `<span style="display:inline-block;border:1px solid ${CYAN};background:rgba(0,184,217,0.12);border-radius:999px;padding:6px 18px;margin:0 5px 10px 5px;font-family:var(--font-sans);font-size:26px;color:${CYAN}">${esc(rr)}</span>`
    ).join('');
    // 9-point inner alignment of the slot block within the container ('free' = top-left).
    const [hW, vW] = align === 'free' || !align ? ['left', 'top'] : parseAlign9(align);
    return `<div style="position:absolute;left:${r.x}px;top:${r.y}px;width:${r.w}px;height:${r.h}px;border:2px dashed ${CYAN};background:rgba(0,184,217,0.06);border-radius:10px;box-sizing:border-box">
      <div style="position:absolute;top:10px;left:14px;font-family:var(--font-sans);font-weight:600;font-size:22px;letter-spacing:0.1em;color:${CYAN}">${name.toUpperCase()}${align && align !== 'free' ? '' : ' · free'}</div>
      <div style="position:absolute;inset:0;display:flex;flex-wrap:wrap;padding:54px 21px 22px;justify-content:${JUST9[hW]};align-items:${ALV9[vW]};align-content:${ALV9[vW]}">${chips}</div>
    </div>`;
  };

  const overlays = Object.keys(rects).map(z => band(z, rects[z], zones[z], tpl.zoneAlign && tpl.zoneAlign[z]));
  return slideDoc(`${bgLayers}\n    ${overlays.join('\n    ')}\n    ${chromeHtml}`,
    { theme, baseFill, guides, raster, dataAttrs: ` data-master-slide="${esc(msId)}" data-template="${esc(templateId)}"` });
}

/** Emit the `templates:` entry for ONE Level-2 template, in templates.md shape,
 *  so an authored change can be pasted back into the canonical md. */
export function templateMd(id, t) {
  const zoneStr = '{ ' + Object.entries(t.zones || {}).map(([z, roles]) => `${z}: [${(roles || []).join(', ')}]`).join(', ') + ' }';
  const L = [`  ${id}:`, `    label: ${t.label || id}`, `    group: ${t.group || 'Content'}`,
    `    master: ${t.master}`, `    zones: ${zoneStr}`];
  if (t.zoneRects && Object.keys(t.zoneRects).length) {
    const r = (o) => `{ x: ${Math.round(o.x)}, y: ${Math.round(o.y)}, w: ${Math.round(o.w)}, h: ${Math.round(o.h)} }`;
    L.push(`    zoneRects: { ${Object.entries(t.zoneRects).map(([z, o]) => `${z}: ${r(o)}`).join(', ')} }`);
  }
  if (t.zoneAlign && Object.keys(t.zoneAlign).length) {
    L.push(`    zoneAlign: { ${Object.entries(t.zoneAlign).map(([z, a]) => `${z}: ${a}`).join(', ')} }`);
  }
  if (t.note) L.push(`    note: ${t.note}`);
  return L.join('\n');
}

/** Emit the `slides:` entry for ONE master slide, in master-slides.md shape, so
 *  an authored change can be pasted back into the canonical md (the deployed site
 *  can't write the repo — this is the commit path, mirroring tokensMd). */
export function masterSlideMd(id, m) {
  const q = (v) => (typeof v === 'string' && v.startsWith('#') ? `"${v}"` : v);
  const bg = m.background || {};
  const bgStr = bg.fill != null
    ? `{ fill: ${bg.fill} }`
    : `{ base: ${bg.base || 'black'}, image: ${bg.image || 'sample'}, transparency: ${bg.transparency || 0} }`;
  const L = [`  ${id}:`, `    label: ${m.label || id}`, `    group: ${m.group || 'Slides'}`,
    `    background: ${bgStr}`];
  if (m.scrim) L.push(`    scrim: true`);
  L.push(`    fg: ${m.fg}`);
  if (m.muted) L.push(`    muted: ${q(m.muted)}`);
  if (m.surface) L.push(`    surface: ${q(m.surface)}`);
  if (m.accent) L.push(`    accent: ${q(m.accent)}`);
  L.push(`    logo: { variant: ${m.logo?.variant || 'dark'} }`);
  L.push(`    furniture: [${(m.furniture || []).join(', ')}]`);
  if (m.slideClass) L.push(`    slideClass: ${m.slideClass}`);
  return L.join('\n');
}

/** Render a full slide HTML document for a master+template+theme. `overrides`
 *  (optional) live-adjusts the zone tokens for the authoring tool: zone band
 *  heights via --zh/--zf on .slide (so `main` reflows automatically) and per-
 *  zone inline padding — overriding the generated CSS without a rebuild. */
export function renderSlideHTML({ masterId, templateId, themeId, page, content, overrides, logoSet, deckTitle }) {
  const master = system.masters[masterId];
  if (!master) throw new Error(`unknown master "${masterId}"`);
  const tplGroup = system.templates[masterId] || {};
  const tpl = templateId ? tplGroup[templateId] : Object.values(tplGroup)[0];
  const themes = system.backgrounds?.themes || {};
  const theme = themes[themeId] || themes[system.backgrounds?.default] || { fill: 'warm-grey', fg: 'black', logo: 'dark', slideClass: 'slide--warm' };

  const zonesPresent = master.zones || ['background', 'main'];
  const framesByZone = {};
  for (const f of (tpl?.frames || [])) (framesByZone[f.zone] ||= []).push(f);

  // Each zone's canvas-space top, so frames authored in canvas coords land right.
  const zh = overrides?.zones?.header?.height ?? system.tokens?.zones?.header?.height ?? 360;
  const zf = overrides?.zones?.footer?.height ?? system.tokens?.zones?.footer?.height ?? 120;
  const hasHeader = zonesPresent.includes('header');
  const zoneTop = { background: 0, header: 0, main: hasHeader ? zh : 0, footer: CANVAS_H - zf };
  const padOf = (z) => {
    const p = overrides?.zones?.[z]?.padding; if (!p) return '';
    return `padding:${p.top || 0}px ${p.right || 0}px ${p.bottom || 0}px ${p.left || 0}px;`;
  };
  const zoneHtml = zonesPresent.map(z => {
    let frames = (framesByZone[z] || []).map(f => renderFrame(f, false, theme, theme.fill, content, zoneTop[z] || 0)).join('');
    // The footer ZONE is a separate, editable layer ABOVE the master chrome —
    // it carries sources/notes (logo/page/copyright stay in the master chrome).
    let st = padOf(z);
    if (z === 'footer') { frames += footerDefault(theme, content); st += 'z-index:11;'; }
    return `<div class="zone zone--${z}" data-zone="${z}"${st ? ` style="${st}"` : ''}>${frames}</div>`;
  }).join('');
  const zhf = overrides?.zones?.header?.height;
  const zff = overrides?.zones?.footer?.height;
  const overrideVars = `${zhf != null ? `--zh:${zhf}px;` : ''}${zff != null ? `--zf:${zff}px;` : ''}`;

  // The overall master layer (logo / symbol / copyright / page) on every slide.
  const chromeHtml = renderCorner(master, theme, page, logoSet, undefined, deckTitle);

  return `<!doctype html>
<html lang="en"><head>
<meta charset="utf-8">
<link rel="stylesheet" href="/brand/slide.css">
<link rel="stylesheet" href="/brand/slide-system.generated.css">
</head><body>
<div class="stage">
  <div class="slide ${esc(theme.slideClass || '')}" data-edited data-theme="${esc(themeId || system.backgrounds?.default || '')}" data-master="${esc(masterId)}" data-template="${esc(tpl?.id || '')}" style="${overrideVars}color:${color('fg', theme, theme.fill)};background:${color(theme.fill, theme, theme.fill)}">
    ${zoneHtml}
    ${chromeHtml}
  </div>
</div>
<script src="/brand/slide-fit.js"></script>
</body></html>`;
}
